import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useUpdateZakazka, useZakazkaDetail } from "@/api/hooks/useZakazky";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import {
  ZakazkaPolia,
  type ZakazkaPoliaHodnoty,
} from "@/components/zakazka/ZakazkaPolia";

export interface ZakazkaEditDialogProps {
  zakazkaId: string | null;
  onOpenChange: (open: boolean) => void;
}

/**
 * Úprava údajov zákazky — číslo, názov, rozmery bez hrán. Rovnaké polia
 * ako Nová zákazka (KOLO 162; klient a termín odišli). Otvára sa zo
 * zoznamu aj z editora.
 */
export function ZakazkaEditDialog({
  zakazkaId,
  onOpenChange,
}: ZakazkaEditDialogProps): JSX.Element {
  const open = zakazkaId !== null;
  const detailQuery = useZakazkaDetail(zakazkaId ?? undefined);
  const updateMutation = useUpdateZakazka();
  const [polia, setPolia] = useState<ZakazkaPoliaHodnoty>({
    cislo: "",
    nazov: "",
    bezHran: true,
  });

  const zakazka = detailQuery.data?.data;

  useEffect(() => {
    if (open && zakazka) {
      setPolia({
        cislo: zakazka.cislo_zakazky,
        nazov: zakazka.nazov ?? "",
        bezHran: zakazka.rozmery_bez_hran ?? true,
      });
    }
  }, [open, zakazka]);

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const cislo = polia.cislo.trim();
    if (!zakazkaId || !cislo) return;
    try {
      await updateMutation.mutateAsync({
        id: zakazkaId,
        data: {
          // Číslo malej zákazky je trvalé (KOLO 200) — server zmenu odmietne
          ...(zakazka?.cislo_z ? {} : { cislo_zakazky: cislo }),
          nazov: polia.nazov.trim() || null,
          rozmery_bez_hran: polia.bezHran,
        },
      });
      toast({ title: "Zákazka uložená", description: cislo });
      onOpenChange(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Chyba pri ukladaní zákazky",
        description: extractErrorMessage(err),
      });
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Upraviť zákazku</DialogTitle>
          <DialogDescription>
            Zmena údajov zadaných pri vytvorení zákazky.
          </DialogDescription>
        </DialogHeader>
        {detailQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Načítavam…</p>
        ) : (
          <form onSubmit={handleSave} className="space-y-4">
            <ZakazkaPolia
              hodnoty={polia}
              onChange={setPolia}
              idPrefix="ze"
              cisloPovinne
              trvaleCislo={Boolean(zakazka?.cislo_z)}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={updateMutation.isPending}
              >
                Zrušiť
              </Button>
              <Button
                type="submit"
                disabled={updateMutation.isPending || !polia.cislo.trim()}
              >
                {updateMutation.isPending ? "Ukladá sa…" : "Uložiť"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
